import { HttpErrorResponse } from '@angular/common/http';
import { buildSavedViewFiltersJson, parseSavedViewFilters } from '../../shared/saved-views/saved-view-filters';
import {
  TEST_PLAN_STATUSES,
  TEST_PLAN_TYPES,
  TestPlan,
  TestPlanExecutionKind,
  TestPlanStatus,
  TestPlanType,
} from '../../models/test-plan.model';
import { TestCase } from '../../models/test-case.model';

/**
 * Filter shape of the test-plans overview, serialized into `SavedView.filtersJson` (NEX-787).
 *
 * `version` bleibt als Feld erhalten, obwohl der Version-Chip mit NEX-1059 entfallen ist:
 * Alt-Ansichten in Produktion tragen einen version-Wert, der beim Laden in die
 * Suche gespiegelt wird (siehe `TestPlansComponent.onSavedViewSelected`). Wuerden
 * wir das Feld hier entfernen, wuerde `JSON.parse` den Wert stumm verwerfen und
 * die Migration ins Leere laufen. Auf der Schreibseite ist `version` immer `null`.
 */
export interface TestPlanSavedViewFilters {
  query: string | null;
  version: string | null;
  assignee: string | null;
  release: string | null;
  status: TestPlanStatus | null;
  tags: string[];
  /** Fehlt in Ansichten ohne Art-Filter. */
  type?: TestPlanType;
}

const EMPTY_TEST_PLAN_SAVED_VIEW_FILTERS: TestPlanSavedViewFilters = {
  query: null,
  version: null,
  assignee: null,
  release: null,
  status: null,
  tags: [],
  type: undefined,
};

export function parseTestPlanSavedViewFilters(filtersJson: string | null): TestPlanSavedViewFilters {
  const { type: savedType, ...filters } = parseSavedViewFilters(filtersJson, EMPTY_TEST_PLAN_SAVED_VIEW_FILTERS);
  const status = filters.status as unknown;
  const tags = filters.tags as unknown;
  const type = TEST_PLAN_TYPES.find((known) => known === savedType);
  return {
    ...filters,
    release: typeof filters.release === 'string' ? filters.release : null,
    status: TEST_PLAN_STATUSES.find((known) => known === status) ?? null,
    tags: Array.isArray(tags) ? tags.filter((tag): tag is string => typeof tag === 'string') : [],
    ...(type ? { type } : {}),
  };
}

/** Pläne einer älteren Antwort tragen keine Art und zählen als Freigabe. */
export function testPlanType(plan: Pick<TestPlan, 'type'>): TestPlanType {
  return plan.type ?? 'FREIGABE';
}

export function buildTestPlanSavedViewFiltersJson(filters: TestPlanSavedViewFilters): string {
  return buildSavedViewFiltersJson(filters);
}

export type PickerExecutionFilter = 'MANUAL' | 'AUTOMATABLE' | 'AUTOMATED';

export const PICKER_EXECUTION_FILTERS: readonly PickerExecutionFilter[] = ['MANUAL', 'AUTOMATABLE', 'AUTOMATED'];

export interface TestPlanPickerSavedViewFilters {
  query: string | null;
  testType: string | null;
  priority: string | null;
  tags: string[];
  execution: PickerExecutionFilter | null;
}

const EMPTY_TEST_PLAN_PICKER_SAVED_VIEW_FILTERS: TestPlanPickerSavedViewFilters = {
  query: null,
  testType: null,
  priority: null,
  tags: [],
  execution: null,
};

export function parseTestPlanPickerSavedViewFilters(filtersJson: string | null): TestPlanPickerSavedViewFilters {
  const filters = parseSavedViewFilters(filtersJson, EMPTY_TEST_PLAN_PICKER_SAVED_VIEW_FILTERS);
  const execution = filters.execution as unknown;
  const tags = filters.tags as unknown;
  return {
    ...filters,
    tags: Array.isArray(tags) ? tags.filter((tag): tag is string => typeof tag === 'string') : [],
    execution: PICKER_EXECUTION_FILTERS.find((known) => known === execution) ?? null,
  };
}

export function buildTestPlanPickerSavedViewFiltersJson(filters: TestPlanPickerSavedViewFilters): string {
  return buildSavedViewFiltersJson(filters);
}

export function realRunQueryParams(planId: string, automated: boolean): Record<string, string> {
  return { plan: planId, art: automated ? 'automatisiert' : 'manuell', laufart: 'echt' };
}

export function caseExecutionKind(tc: Pick<TestCase, 'automationStatus'>): TestPlanExecutionKind {
  return tc.automationStatus === 'AUTOMATED' ? 'AUTOMATED' : 'MANUAL';
}

/** Mit gesetzter Art bleibt nur „Nur automatisierbare“ wirksam; ein anderer Wert hätte kein Bedienelement und leerte die Liste unbemerkt. */
export function matchesExecutionFilter(
  tc: Pick<TestCase, 'automationStatus'>,
  filter: PickerExecutionFilter | '',
  planKind: TestPlanExecutionKind | null,
): boolean {
  if (filter === 'AUTOMATABLE' && planKind !== 'AUTOMATED') return tc.automationStatus === 'AUTOMATABLE';
  if (!filter || planKind) return true;
  return caseExecutionKind(tc) === filter;
}

/** Wie `TestPlanTags` im Backend. */
export function normalizeRuleTags(tags: readonly string[]): string[] {
  return [...new Set(tags.map((tag) => tag.trim().toLowerCase()).filter(Boolean))];
}

export function sameRule(left: readonly string[], right: readonly string[]): boolean {
  const known = new Set(normalizeRuleTags(left));
  const other = normalizeRuleTags(right);
  return other.length === known.size && other.every((tag) => known.has(tag));
}

export function matchesRule(tc: { tags?: readonly string[] | null }, ruleTags: readonly string[]): boolean {
  return normalizeRuleTags(tc.tags ?? []).some((tag) => ruleTags.includes(tag));
}

export interface RunSummary {
  key: string;
  count: number;
  lastRunAt: string | null;
}

export function localIsoDate(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function isOverdue(plan: TestPlan, today: string): boolean {
  return plan.status !== 'ABGESCHLOSSEN' && !!plan.dueDate && plan.dueDate < today;
}

export function runSummary(plan: TestPlan): RunSummary {
  const count = plan.runCount ?? 0;
  if (count === 0) {
    return { key: 'testPlans.list.noRun', count: 0, lastRunAt: null };
  }
  const lastRunAt = plan.lastRunAt ?? null;
  const key = count === 1 ? 'testPlans.list.runsOne' : 'testPlans.list.runsOther';
  return { key: lastRunAt ? key : `${key}NoDate`, count, lastRunAt };
}

export function writeErrorKey(error: unknown, fallbackKey: string): string {
  return error instanceof HttpErrorResponse && error.status === 403 ? 'testPlans.forbidden' : fallbackKey;
}

export function archiveErrorKey(error: unknown): string {
  if (error instanceof HttpErrorResponse && error.status === 409) {
    return 'testPlans.list.archiveDialog.notCompleted';
  }
  return writeErrorKey(error, 'testPlans.list.archiveDialog.error');
}
